import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription, map, take, tap } from 'rxjs';
import { BlockUI, NgBlockUI, BlockUIModule } from 'ng-block-ui';
import { Store } from '@ngrx/store';

import { BlockTemplateComponent } from 'src/app/@theme/@components';
import { AppState } from 'src/app/@store/app.reducers';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { RegistroAgenteFormComponent } from './registro-agente-form/registro-agente-form.component';
import { RegistroProveedorPromotorFormComponent } from './registro-proveedor-promotor-form/registro-proveedor-promotor-form.component';
import { RegistroAseguradoFormComponent } from './registro-asegurado-form/registro-asegurado-form.component';
import { RegistroReaseguradorFormComponent } from './registro-reasegurador-form/registro-reasegurador-form.component';
import { RegistroEmpleadoEjecutivoFormComponent } from './registro-empleado-ejecutivo-form/registro-empleado-ejecutivo-form.component';
import { MatDialog } from '@angular/material/dialog';
import { RegistroInformativoDialogComponent} from './dialogs/registro-informativo-dialog/registro-informativo-dialog.component';

type RegistroStep = 1 | 2 | 3;
type CardPosition = 'center' | 'right';

export type RegistroPerfilKey =
  | 'A'
  | 'Ag'
  | 'Proveedor'
  | 'Promotor'
  | 'Reasegurador'
  | 'AgUdi'
  | 'Ejecutivo'
  | 'Empleado';

interface RegistroStepItem {
  index: RegistroStep;
  label: string;
}

interface RegistroPerfilOption {
  key: RegistroPerfilKey;
  label: string;
  icon: string;
  description: string;
}

@Component({
    selector: 'app-registro-stepper',
    templateUrl: './registro-stepper.component.html',
    styleUrls: ['./registro-stepper.component.scss'],
    imports: [
      BlockUIModule, 
      NgClass, 
      MatIcon, 
      RegistroAgenteFormComponent, 
      RegistroProveedorPromotorFormComponent, 
      RegistroAseguradoFormComponent, 
      RegistroReaseguradorFormComponent,
      RegistroEmpleadoEjecutivoFormComponent
    ]
})
export class RegistroStepperComponent implements OnInit, OnDestroy {
  @ViewChild('registroScroll') private registroScroll?: ElementRef<HTMLDivElement>;
  readonly cardPosition: CardPosition = 'center';

  readonly steps: RegistroStepItem[] = [
    { index: 1, label: 'Perfil' },
    { index: 2, label: 'Datos' },
    { index: 3, label: 'Confirmación' }
  ];

  readonly profiles: RegistroPerfilOption[] = [
    {
      key: 'A',
      label: 'Asegurado',
      icon: 'verified_user',
      description: 'Consulta pólizas, trámites y servicios.'
    },
    {
      key: 'Ag',
      label: 'Agente',
      icon: 'headset_mic',
      description: 'Gestiona clientes y operaciones.'
    },
    {
      key: 'Proveedor',
      label: 'Proveedor',
      icon: 'local_shipping',
      description: 'Accede a servicios para proveedores.'
    },
    {
      key: 'Promotor',
      label: 'Promotor',
      icon: 'record_voice_over',
      description: 'Administra información de promoción.'
    },
    {
      key: 'Reasegurador',
      label: 'Reasegurador',
      icon: 'business',
      description: 'Consulta operaciones relacionadas.'
    },
    {
      key: 'AgUdi',
      label: 'Agente UDI',
      icon: 'directions_car',
      description: 'Registro especializado para Agente UDI.'
    },
    {
      key: 'Ejecutivo',
      label: 'Ejecutivo',
      icon: 'person',
      description: 'Acceso para ejecutivos Atlas.'
    },
    {
      key: 'Empleado',
      label: 'Empleado',
      icon: 'work',
      description: 'Acceso para colaboradores internos.'
    }
  ];

  readonly benefits: string[] = [
    'Accede a tus pólizas y documentación',
    'Realiza trámites y da seguimiento',
    'Reporta siniestros 24/7',
    'Atención y soporte personalizado'
  ];

  currentStep: RegistroStep = 1;
  selectedProfile: RegistroPerfilKey = 'A';
  imagen = 'assets/img/1.jpg';
  usuarioRegistrado = '';
  formularioModificado = false;
  @BlockUI() blockUI!: NgBlockUI;

  template = 'corporativo';
  blockTemplate = BlockTemplateComponent;

  private readonly subsReduxUI = new Subscription();
  private readonly totalBackgroundImages = 5;
  private backgroundTimerId?: ReturnType<typeof setInterval>;

  private readonly avisoPrivacidadUrl = 'https://www.segurosatlas.com.mx/aviso-de-privacidad.html';
  private readonly contactanosUrl = 'https://www.segurosatlas.com.mx/contactanos.html';

  constructor(
    private readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.actualizarImagenAleatoria();

    this.backgroundTimerId = setInterval(() => {
      this.actualizarImagenAleatoria();
    }, 30000);

    this.subsReduxUI.add(
      this.store.select('ui').subscribe(data => {
        const isLoading = data?.block?.isLoading;

        if (isLoading === true) {
          this.blockUI.start('Cargando...');
          return;
        }

        if (isLoading === false) {
          this.blockUI.reset();
        }
      })
    );
  }

  ngOnDestroy(): void {
    if (this.backgroundTimerId) {
      clearInterval(this.backgroundTimerId);
    }

    this.subsReduxUI.unsubscribe();
    this.blockUI.reset();
  }

  selectProfile(profileKey: RegistroPerfilKey): void {
    this.selectedProfile = profileKey;
  }

  get nombrePerfilSeleccionado(): string {
    return this.profiles.find(
      profile => profile.key === this.selectedProfile
    )?.label || 'Usuario';
  }

  comenzarRegistro(): void {
    if (!this.selectedProfile) {
      return;
    }

    this.formularioModificado = false;
    this.cambiarStep(2);
  }

  regresarAPerfil(): void {
    this.solicitarSalida(() => {
      this.cambiarStep(1);
    });
  }

  irAConfirmacion(usuario?: string): void {
    this.usuarioRegistrado = usuario || '';
    this.formularioModificado = false;
    this.cambiarStep(3);
  }

  regresarLogin(): void {
    this.solicitarSalida(() => {
      const usuario = `${this.usuarioRegistrado || ''}`.trim();

      if (usuario) {
        sessionStorage.setItem('AC-REG-USUARIO', usuario);
      }

      this.router.navigate(['/login']);
    });
  }

  goToStep(step: RegistroStep): void {
    if (!this.canNavigateToStep(step)) {
      return;
    }

    if (step === 1 && this.currentStep === 2) {
      this.regresarAPerfil();
      return;
    }

    this.cambiarStep(step);
  }

  canNavigateToStep(step: RegistroStep): boolean {
    if (step === 1) {
      return true;
    }

    if (step === 2) {
      return this.currentStep === 2;
    }

    return false;
  }

  isStepActive(step: RegistroStep): boolean {
    return this.currentStep === step;
  }

  isStepCompleted(step: RegistroStep): boolean {
    return step < this.currentStep;
  }

  trackByProfile(_: number, profile: RegistroPerfilOption): RegistroPerfilKey {
    return profile.key;
  }

  abrirAvisoPrivacidad(): void {
    this.abrirUrlExterna(this.avisoPrivacidadUrl);
  }

  abrirContactanos(): void {
    this.abrirUrlExterna(this.contactanosUrl);
  }

  actualizarEstadoFormulario(modificado: boolean): void {
    if (this.currentStep !== 2) {
      return;
    }

    this.formularioModificado = modificado;
  }

  confirmarSalidaRuta(): boolean | Observable<boolean> {
    if (!this.debeConfirmarSalida()) {
      return true;
    }

    return this.abrirConfirmacionSalida().pipe(
      tap(confirmado => {
        if (confirmado) {
          this.formularioModificado = false;
        }
      })
    );
  }

  private solicitarSalida(accion: () => void): void {
    if (!this.debeConfirmarSalida()) {
      accion();
      return;
    }

    this.abrirConfirmacionSalida().subscribe(confirmado => {
      if (!confirmado) {
        return;
      }

      this.formularioModificado = false;
      accion();
    });
  }

  private debeConfirmarSalida(): boolean {
    return this.currentStep === 2 && this.formularioModificado;
  }

  private abrirConfirmacionSalida(): Observable<boolean> {
    return this.dialog
      .open(RegistroInformativoDialogComponent, {
        width: '440px',
        maxWidth: 'calc(100vw - 32px)',
        autoFocus: false,
        disableClose: true,
        data: {
          type: 'warning',
          icon: 'error_outline',
          title: '¿Estás seguro de salir?',
          message:
            'Se perderá la información que hayas ingresado en el formulario.',
          showCancelButton: true,
          confirmText: 'Salir',
          cancelText: 'Permanecer'
        }
      })
      .afterClosed()
      .pipe(
        take(1),
        map(resultado => resultado === true)
      );
  }

  private actualizarImagenAleatoria(): void {
    const imageNumber = Math.floor(Math.random() * this.totalBackgroundImages) + 1;
    this.imagen = `assets/img/${imageNumber}.jpg`;
  }

  private abrirUrlExterna(url: string): void {
    if (!url) {
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private cambiarStep(step: RegistroStep): void {
    this.currentStep = step;
    this.scrollRegistroToTop();
  }

  private scrollRegistroToTop(): void {
    setTimeout(() => {
      this.registroScroll?.nativeElement.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      });
    });
  }
}